/**
 * Shared common utilities: URL params, domain, odds formatting.
 * Migrated from caribe7/lib/helpers/common.js.
 */

/**
 * Gets a URL query parameter by name. Pure URL parsing, no caching.
 *
 * @param sParam - The parameter name (e.g. 's', 'domain')
 * @returns The parameter value, or null if not found
 */
export function getUrlParameter(sParam: string): string | true | null {
  if (typeof window === 'undefined') return null;

  const sPageURL = window.location.search.substring(1);
  const sURLVariables = sPageURL.split('&');

  for (let i = 0; i < sURLVariables.length; i++) {
    const sParameterName = sURLVariables[i].split('=');
    if (sParameterName[0] === sParam) {
      const val = sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
      if (val === '' || val === null) return null;
      return val;
    }
  }

  return null;
}

/**
 * Domain for API/realtime skin lookup. Single source of truth: URL param when embedded,
 * hostname when standalone.
 *
 * @returns Domain string or null
 */
export function getDomainForApi(): string | null {
  if (typeof window === 'undefined') return null;
  const urlDomain = getUrlParameter('domain');
  if (urlDomain && typeof urlDomain === 'string' && urlDomain.trim()) {
    return urlDomain.trim();
  }
  const hostname = window.location?.hostname;
  if (hostname && typeof hostname === 'string' && hostname.trim()) {
    return hostname.trim();
  }
  return null;
}

function inferCaribe7TldFromHostname(hostname: string | undefined): 'local' | 'dev' | 'com' | null {
  const h = String(hostname || '').trim().toLowerCase();
  if (!h) return null;
  if (h === 'localhost' || h.endsWith('.localhost')) return 'local';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return 'local';
  if (h.endsWith('.caribe7.local')) return 'local';
  if (h.endsWith('.caribe7.dev')) return 'dev';
  if (h.endsWith('.caribe7.com')) return 'com';
  return null;
}

function protocolForTld(tld: string): string {
  if (tld === 'local') return 'http';
  if (tld === 'dev' || tld === 'com') return 'https';
  return 'https';
}

function deriveVirtualSportsApiUrl(): string {
  if (typeof window === 'undefined') return '';
  const tld = inferCaribe7TldFromHostname(window.location?.hostname);
  if (!tld) return '';
  const proto = protocolForTld(tld);
  return `${proto}://virtual-sports-api.caribe7.${tld}`.replace(/\/+$/, '');
}

/**
 * Gets the base URL for the virtual-sports API (virtual-core).
 * Uses REACT_APP_API_URL if set, otherwise derives from hostname.
 */
export async function getBaseUrl(): Promise<string> {
  const override = process.env.REACT_APP_API_URL;
  if (override && String(override).trim()) {
    return String(override).trim().replace(/\/+$/, '');
  }
  return deriveVirtualSportsApiUrl();
}

/**
 * Returns true if the value is falsy (null, undefined, empty string, empty object, etc.).
 *
 * @param check - Value to check
 * @returns True if falsy
 */
export function isFalsy(check: unknown): boolean {
  if (!check) return true;
  if (typeof check === 'string') {
    if (!check.trim()) return true;
  }
  if (typeof check === 'object' && check !== null) {
    if (!Object.values(check).length) return true;
  }
  return false;
}

/**
 * Returns true if obj[method] is a function.
 */
export function isCallable(obj: unknown, method: string): boolean {
  if (isFalsy(obj) || isFalsy(method)) return false;
  return typeof (obj as Record<string, unknown>)[method] === 'function';
}

/**
 * Return currency symbol for a given currency code.
 */
export function getCurrencySymbol(code?: string): string {
  const map: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
  return (code && map[String(code).toUpperCase()]) || code || '';
}

/**
 * Convert decimal odds to fractional format (e.g. 2.5 -> "3/2").
 */
function decimalToFractional(decimalOdd: number | string): string {
  const floatOdd = parseFloat(String(decimalOdd));
  if (isNaN(floatOdd) || floatOdd <= 1) {
    return String(decimalOdd);
  }
  const profitRatio = floatOdd - 1;
  const tolerance = 0.001;
  let bestNumerator = 1;
  let bestDenominator = 1;
  let bestError = Math.abs(profitRatio - bestNumerator / bestDenominator);

  for (let den = 1; den <= 100; den++) {
    const num = Math.round(profitRatio * den);
    const error = Math.abs(profitRatio - num / den);
    if (error < bestError) {
      bestError = error;
      bestNumerator = num;
      bestDenominator = den;
      if (error < tolerance) break;
    }
  }

  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(bestNumerator, bestDenominator);
  const simplifiedNum = bestNumerator / divisor;
  const simplifiedDen = bestDenominator / divisor;
  return `${simplifiedNum}/${simplifiedDen}`;
}

const PRINT_ODD_FORMATS = ['decimal', 'fractional', 'american'] as const;

/**
 * Format odds for display (decimal, fractional, or american).
 *
 * @param odd - Odds value (number or string)
 * @param odds_format - One of 'decimal', 'fractional', 'american'
 * @returns Formatted string
 */
export function printOdd(odd: number | string, odds_format: string = 'decimal'): string {
  const format =
    odds_format && typeof odds_format === 'string'
      ? odds_format.toLowerCase().trim()
      : 'decimal';
  const safeFormat = PRINT_ODD_FORMATS.includes(format as (typeof PRINT_ODD_FORMATS)[number])
    ? (format as (typeof PRINT_ODD_FORMATS)[number])
    : 'decimal';

  if (safeFormat === 'decimal') {
    return parseFloat(String(odd)).toFixed(2);
  }
  if (safeFormat === 'fractional') {
    return decimalToFractional(odd);
  }
  if (safeFormat === 'american') {
    const floatOdd = parseFloat(String(odd));
    if (floatOdd < 2) {
      return '-' + Math.floor(-100 / (floatOdd - 1)).toString();
    }
    if (floatOdd >= 2) {
      return '+' + Math.floor((floatOdd - 1) * 100).toString();
    }
  }
  return parseFloat(String(odd)).toFixed(2);
}
